import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import type {
  MarkirovkaReconSessionFilters,
  MarkirovkaReconSessionsList,
  MarkirovkaReconSession,
  MarkirovkaDiscrepanciesList,
  MarkirovkaDiscrepancyFilters,
  MarkirovkaOpenSessionPayload,
  MarkirovkaUpdateSessionPayload,
  MarkirovkaReconCisList,
  MarkirovkaCisKind,
} from '../types/markirovka-stock.types'

const RECON_QUERY_KEY = 'markirovka-recon'

export const useMarkirovkaReconSessions = (
  filters: MarkirovkaReconSessionFilters = {},
) => {
  return useQuery({
    queryKey: [RECON_QUERY_KEY, 'sessions', filters],
    queryFn: async (): Promise<MarkirovkaReconSessionsList> =>
      apiClient.getMarkirovkaReconSessions(filters),
    staleTime: 30 * 1000,
    retry: 1,
  })
}

export const useMarkirovkaReconSession = (
  sessionId: string | null,
  enabled = true,
) => {
  return useQuery({
    queryKey: [RECON_QUERY_KEY, 'session', sessionId],
    queryFn: async (): Promise<MarkirovkaReconSession> =>
      apiClient.getMarkirovkaReconSession(sessionId as string),
    enabled: !!sessionId && enabled,
    // Живой счётчик scanned_in_period — обновляем чаще, пока документ открыт.
    refetchInterval: enabled ? 15 * 1000 : false,
    staleTime: 5 * 1000,
    retry: 1,
  })
}

export const useMarkirovkaReconDiscrepancies = (
  sessionId: string | null,
  filters: MarkirovkaDiscrepancyFilters = {},
) => {
  return useQuery({
    queryKey: [RECON_QUERY_KEY, 'discrepancies', sessionId, filters],
    queryFn: async (): Promise<MarkirovkaDiscrepanciesList> =>
      apiClient.getMarkirovkaReconDiscrepancies(sessionId as string, filters),
    enabled: !!sessionId,
    staleTime: 30 * 1000,
    retry: 1,
  })
}

export const useOpenReconSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      payload: MarkirovkaOpenSessionPayload = {},
    ): Promise<MarkirovkaReconSession> =>
      apiClient.openMarkirovkaReconSession(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECON_QUERY_KEY] })
    },
  })
}

export const useUpdateReconSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      id: string
      payload: MarkirovkaUpdateSessionPayload
    }): Promise<MarkirovkaReconSession> =>
      apiClient.updateMarkirovkaReconSession(vars.id, vars.payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECON_QUERY_KEY] })
    },
  })
}

export const useCloseReconSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<MarkirovkaReconSession> =>
      apiClient.closeMarkirovkaReconSession(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECON_QUERY_KEY] })
    },
  })
}

// «Заполнить учётный остаток»: синк edo + живой расчёт (admin).
export const useSyncReconStock = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => apiClient.syncMarkirovkaReconStock(id),
    // Снимок меняет карточку документа и при неудаче (snapshot_status=failed).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [RECON_QUERY_KEY] })
    },
  })
}

// «Заполнить фактические остатки»: живой расчёт без закрытия.
export const usePreviewReconSession = () => {
  return useMutation({
    mutationFn: async (id: string) => apiClient.getMarkirovkaReconPreview(id),
  })
}

// Поштучный список CIS (на вывод / излишек / в наличии).
export const useMarkirovkaReconCis = (
  sessionId: string | null,
  kind: MarkirovkaCisKind,
  page: number,
  enabled: boolean,
  limit = 100,
) => {
  return useQuery({
    queryKey: [RECON_QUERY_KEY, 'cis', sessionId, kind, page, limit],
    queryFn: async (): Promise<MarkirovkaReconCisList> =>
      apiClient.getMarkirovkaReconCis(sessionId as string, {
        kind,
        page,
        limit,
      }),
    enabled: !!sessionId && enabled,
    staleTime: 30 * 1000,
    retry: 1,
  })
}

export const useDeleteReconSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      apiClient.deleteMarkirovkaReconSession(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECON_QUERY_KEY] })
    },
  })
}

export const useExportReconSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<MarkirovkaReconSession> =>
      apiClient.exportMarkirovkaReconSession(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [RECON_QUERY_KEY] })
    },
  })
}
